import React, { useMemo, useState, useContext, useEffect } from 'react';
import appConfig from 'app-config';
import { Loader, useModal } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { WizardPage } from '@xengage/gw-portals-wizard-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { MLModalService } from 'ml-portals-services-react';
import { FormModalPopover, platformMessages } from 'ml-components-platform-react';
import { RequiredSignaturesComponent } from 'gw-capability-policychange-common-react';
import { MLFormService } from 'ml-services-common-react';

import metadata from './MLRewriteRequiredSignaturesPage.metadata.json5';
import messages from './MLRewriteRequiredSignaturesPage.messages';
import styles from './MLRewriteRequiredSignaturesPage.module.scss'
import ApplySignatureTypeToAllPopupMetadata from './ApplySignatureTypeToAllPopup.metadata.json5';

function MLRewriteRequiredSignaturesPage(props) {
    const modalApi = useModal();

    const {
        updateWizardData, wizardData, goNext, currentStep
    } = props;
    const {
        MLFormNumberWildcards
    } = appConfig;
    const { submissionVM } = wizardData;
    const { authHeader } = useAuthentication();
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const { UserService } = useDependencies('UserService');
    const { isComponentValid, onValidate } = useValidation('rewriteEffectiveDate');
    const [isLoading, setIsLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState({});
    const [promptToApplySignature, setPromptToApplySignature] = useState(true);
    const [isUserRetentionAgent, setIsUserRetentionAgent] = useState(false);
    const isGpa = true;

    const { refreshData } = useDataRefresh();
    const ModalService = MLModalService();

    const SIGNATURETYPE_TYPELIST_FULL = viewModelService.productMetadata.get('pc').types.getTypelist('SignatureType_ML')

    const shouldSkip = () => {
        return !!currentStep.stepHidden
    }

    useEffect(() => {
        if (shouldSkip()) {
            goNext();
        } else {
            UserService.getCurrentUser(authHeader).then((user) => {
                setIsUserRetentionAgent(user.isIA)
                setCurrentUser(user);
                setIsLoading(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const allForms = submissionVM.forms_ML;

    const requiredSignaturesForms = useMemo(() => {
        return allForms.filter((form) => {
            return form.esigned_ML.value === true && !(form.verbalAuthorization_ML.value === true && form.isSigned.value === true);
        })
    }, [allForms]);

    const isSignatureOnFileAvailable = (form) => {
        return isGpa && (currentUser.isIA || currentUser.isPCS || currentUser.isMetGA)
            && (form.formNumber.value.startsWith(MLFormNumberWildcards.AUTO_APPLICATION_FORM)
                || form.formNumber.value.startsWith(MLFormNumberWildcards.HOME_APPLICATION_FORM)
                || form.formNumber.value.startsWith(MLFormNumberWildcards.UMBRELLA_APPLICATION_FORM));
    };

    const shouldShowLanguageOption = (form) => {
        return false && form === null;
        // TO DO: need new Spanish indicator in Form Pattern to support Language Option
        // return form.signatureType && form.signatureType.value && (form.signatureType.value.code === 'agent_esignature' || form.signatureType.value.code === 'hard_copy') && form.formNumber.value.indexOf('SP-') != -1;
    };

    const signatureTypeList = SIGNATURETYPE_TYPELIST_FULL.codes
        .filter((sigType) => { return sigType.code !== 'verbal' && sigType.code !== 'signature_on_file'; });
    const appSignatureTypeList = SIGNATURETYPE_TYPELIST_FULL.codes
        .filter((sigType) => { return sigType.code !== 'verbal'; });


    const shouldShowSecondEmail = (form) => {
        return form.secondEmailRequired.value;
    };

    const clearActionFields = (form) => {
        if (!form.signatureType.value) {
            return;
        }
        if (form.signatureType.value.code !== 'fax') {
            form.faxNumber.value = null;
        }
        if (form.signatureType.value.code !== 'email' && form.signatureType.value.code !== 'esignature') {
            form.namedInsuredEmail.value = null;
            form.secondEmail.value = null;
        }
        if (form.signatureType.value.code !== 'usps' && form.signatureType.value.code !== 'hard_copy' &&
            form.signatureType.value.code !== 'agent_esignature') {
            form.formLanguage.value = null;
        }
    };

    const signatureTypeChange = (rsForm) => {
        if (requiredSignaturesForms.length > 1) {
            if (rsForm.signatureType.value && promptToApplySignature) {
                const componentProps = {
                    title: messages.applySignature,
                    uiProps: ApplySignatureTypeToAllPopupMetadata.pageContent,
                    actionBtnLabel: platformMessages.mlYes,
                    cancelBtnLabel: platformMessages.mlNo,
                    onSaveAction: () => {
                        requiredSignaturesForms.forEach((form) => {
                            form.signatureType.value = rsForm.signatureType.value;
                            if (isUserRetentionAgent && rsForm.signatureType.value.code !== 'esignature') {
                                form.isSigned.value = true;
                                form.signedDateTime.value = new Date();
                            }
                            updateWizardData(wizardData);
                        });
                        return true;
                    }
                };
                modalApi.showModal(
                    <FormModalPopover {...componentProps} />
                );
                setPromptToApplySignature(false);
            }
        }

        // when apply signature type to all list select no
        if (isUserRetentionAgent && !rsForm.isSigned.value && rsForm.signatureType.value.code !== 'esignature') {
            rsForm.isSigned.value = true;
            rsForm.signedDateTime.value = new Date();
        }

        refreshData();
    };

    const checkIfEmailsAreDuplicated = (form) => {
        if (shouldShowSecondEmail(form)) {
            if (form.namedInsuredEmail && form.secondEmail) {
                return form.namedInsuredEmail.value === form.secondEmail.value;
            }
        }
        return false;
    };

    const checkActionsCompleted = () => {
        return requiredSignaturesForms.every((form) => {
            if (!form.signatureType.value) {
                return false;
            }
            if (form.signatureType.value.code === 'fax' && !form.faxNumber.value) {
                return false;
            }
            if (form.signatureType.value.code === 'email' || form.signatureType.value.code === 'esignature') {
                if (checkIfEmailsAreDuplicated(form)) {
                    return false;
                }
            }
            if (shouldShowLanguageOption(form) && !form.formLanguage.value) {
                return false;
            }
            return true;
        });
    };

    const shouldShowSecondMobile = (form) => {
            return form?.secondaryMobileNumberRequired?.value || shouldShowExcludedMobile(form);
        }
    
    const shouldShowExcludedMobile = (form) => {
        if (!_.isUndefined(form?.esignatureOptions?.value?.code) && form?.esignatureOptions?.value?.code === 'Text' && !_.isUndefined(form?.excludedDriverMobileNumberRequired?.value) && form?.excludedDriverMobileNumberRequired?.value) {
            return true;
        }
        return false;
    };

    const disableRequiredSignaturesNext = () => {
        // disable if any of the forms do not have signature type selected
        return !!requiredSignaturesForms.find((form) => { return !form.signatureType.value; }) || !checkActionsCompleted()
    };

    const onNext = () => {
        const updateFormPromise = MLFormService.updateForms(submissionVM.jobNumber.value, submissionVM.forms_ML.value, authHeader);
        ModalService.showProgressDialog(platformMessages.loading, updateFormPromise);
        updateFormPromise.then(() => {
            goNext();
        })
    }

    const overrideProps = {
        requiredSignatures: {
            requiredSignaturesForms,
            isSignatureOnFileAvailable,
            appSignatureTypeList,
            signatureTypeList,
            shouldShowLanguageOption,
            clearActionFields,
            signatureTypeChange,
            checkIfEmailsAreDuplicated,
            shouldShowSecondEmail,
            shouldShowSecondMobile,
            updateSecondaryInsuredEmail: () => { },
            onValidate
        },
        nextButton: {
            disabled: disableRequiredSignaturesNext()
        },
        nextDescription: {
            content: <>{translator(messages.nextDescription, { strong: (chunks) => <strong>{chunks}</strong> })}</>
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onNext
        },
        resolveComponentMap: {
            RequiredSignaturesComponent
        }
    }

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    return (
        <WizardPage
            showCancel={false}
            showNext={false}
            showPrevious={false}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={wizardData}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                onValidationChange={onValidate}

            />
        </WizardPage>
    );
}

export default MLRewriteRequiredSignaturesPage;